import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  BoardResponse,
  BoardColumn,
  TaskCard,
  TaskCrudResponse,
  TaskLabel,
} from '../services/api/board';
import { createTask } from '../services/api/board';

export interface CreateTaskInput {
  projectId: string;
  taskStateId: number;
  name: string;
  description?: string | null;
  budget?: number | null;
  hardLimit?: boolean;
  plannedHours?: number | null;
  milestoneId?: number | null; // Phase 31 D-12 — per-cell create lands task in the right swimlane row
  tierId?: number | null; // Phase 49 Plan 49-05 — RC-01 gate: required for SLA Task projects
  /** Pre-resolved tier metadata for instant optimistic badge render. Supplied by AddTaskInlineForm
   * when isSlaProject is true. Not required — TaskModal may omit it (tier badge renders after invalidation). */
  tierData?: { id: number; orderIndex: number; name: string } | null;
  /** MKS-202 — labels picked on the create form; the server applies them inside the create transaction. */
  labelIds?: number[];
  /** Pre-resolved label metadata for the optimistic card's chips, same role as `tierData`. */
  labelData?: TaskLabel[];
}

interface MutationContext {
  previous: BoardResponse | undefined;
  tempId: number;
}

/**
 * Phase 24 Plan 24-08 — internal task create. Optimistic prepend of a
 * temp-id task row to the target column; on success, replace the temp row
 * with the server-issued task. On error, restore the pre-mutation snapshot.
 *
 * Cache key is locked to ['board', projectId, 'internal'] — internal
 * mutations never touch the external cache slot (D-12 / Pitfall 6).
 *
 * WARNING 5 — `mutationFn` throws `Error('projectId required')` when
 * `input.projectId` is falsy. The form is mounted only when the route param
 * is present, but the defensive guard prevents NaN projectId from leaking
 * into the optimistic card and corrupting the cache.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation<TaskCrudResponse, Error, CreateTaskInput, MutationContext>({
    mutationFn: async (input) => {
      if (!input.projectId) {
        throw new Error('projectId required');
      }
      return createTask(input.projectId, {
        taskStateId: input.taskStateId,
        name: input.name,
        description: input.description ?? null,
        ...(input.budget !== undefined ? { budget: input.budget } : {}),
        ...(input.hardLimit !== undefined ? { hardLimit: input.hardLimit } : {}),
        ...(input.plannedHours !== undefined ? { plannedHours: input.plannedHours } : {}),
        ...(input.milestoneId !== undefined ? { milestoneId: input.milestoneId } : {}), // Phase 31 D-12
        ...(input.tierId !== undefined ? { tierId: input.tierId } : {}), // Phase 49 Plan 49-05
        // MKS-202 — omit the key entirely when nothing was picked, so an
        // unflagged create sends exactly the body it always sent.
        ...(input.labelIds && input.labelIds.length > 0 ? { labelIds: input.labelIds } : {}),
      });
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ['board', input.projectId, 'internal'] });
      const previous = queryClient.getQueryData<BoardResponse>([
        'board',
        input.projectId,
        'internal',
      ]);
      const tempId = -Date.now(); // negative — server ids are positive
      if (previous) {
        const optimistic = applyOptimisticCreate(previous, input, tempId);
        queryClient.setQueryData<BoardResponse>(
          ['board', input.projectId, 'internal'],
          optimistic,
        );
      }
      return { previous, tempId };
    },
    onSuccess: (serverTask, input, context) => {
      // Reconcile temp id → server id within the same column.
      if (!context) return;
      const current = queryClient.getQueryData<BoardResponse>([
        'board',
        input.projectId,
        'internal',
      ]);
      if (!current) return;
      const reconciled: BoardResponse = {
        columns: current.columns.map((col) => ({
          ...col,
          tasks: col.tasks.map((t) => {
            if (t.id !== context.tempId) return t;
            // Phase 49 Plan 49-05: preserve the optimistic tier from the
            // temp card — POST response only carries tierId (not nested tier
            // object). The follow-up invalidateQueries (onSettled) will
            // replace this with the server-projected tier after board refetch.
            // MKS-202 — labels ride along for the same reason the tier does:
            // the POST response carries neither, and the board refetch below
            // replaces both with the server-projected values.
            return { ...serverTaskToCardRow(serverTask), tier: t.tier, labels: t.labels };
          }),
        })),
      };
      queryClient.setQueryData<BoardResponse>(
        ['board', input.projectId, 'internal'],
        reconciled,
      );
    },
    onError: (_err, vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'internal'], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({
        queryKey: ['board', input.projectId, 'internal'],
      });
    },
  });
}

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit testing.
// ---------------------------------------------------------------------------

export function applyOptimisticCreate(
  board: BoardResponse,
  input: CreateTaskInput,
  tempId: number,
): BoardResponse {
  // WARNING 5 — input.projectId is guaranteed non-empty by mutationFn
  // (which throws on falsy projectId before this helper runs). The `|| 0`
  // fallback below is belt-and-suspenders insurance against direct callers
  // of applyOptimisticCreate (e.g. unit tests with malformed fixtures) so
  // the cache never carries a NaN projectId.
  const projectIdNum = Number(input.projectId) || 0;
  const optimisticCard: TaskCard = {
    id: tempId,
    name: input.name,
    code: null,
    description: input.description ?? null,
    taskStateId: input.taskStateId,
    isExternal: false,
    createdAt: new Date().toISOString(),
    projectId: projectIdNum,
    milestoneId: input.milestoneId ?? null, // Phase 31 D-12 — auto-fill from the creating cell (defaults to null when omitted)
    budget: input.budget ?? null,
    hardLimit: input.hardLimit ?? false,
    plannedHours: input.plannedHours ?? null,
    reportedHours: 0,
    canFlipVisibility: true, // optimistic: caller is internal member
    // Phase 49 Plan 49-05 — pre-fill tier metadata for instant badge render
    tier: input.tierId != null && input.tierData ? input.tierData : null,
    // MKS-202 — same trick for the label chips; replaced by the server's own
    // labels on the onSettled board refetch.
    labels: input.labelData ?? [],
  };
  const next: BoardColumn[] = board.columns.map((col) =>
    col.taskStateId === input.taskStateId
      ? { ...col, tasks: [...col.tasks, optimisticCard] }
      : col,
  );
  return { columns: next };
}

export function serverTaskToCardRow(t: TaskCrudResponse): TaskCard {
  return {
    id: t.id,
    name: t.name,
    code: t.code,
    description: t.description,
    taskStateId: t.taskStateId,
    isExternal: t.isExternal,
    createdAt: t.createdAt,
    projectId: t.projectId,
    milestoneId: t.milestoneId, // Phase 31 D-12 (CR-02/CR-06) — keep the swimlane row assignment from the server response
    budget: t.budget,
    hardLimit: t.hardLimit,
    plannedHours: t.plannedHours,
    reportedHours: 0,
    // canFlipVisibility is not in the POST/PATCH/DELETE response schema
    // (taskMutationResponseSchema does not enumerate it). The just-active
    // actor must be admin / PM / ProjectAssignment holder per D-03 to
    // reach this code (server-side gate via assertInternalProjectMembership
    // / requireAdminOrProjectMember), and all three actor types CAN flip
    // visibility — so 'true' is correct here as a temporary value. On the
    // next board refetch (onSettled invalidate fires
    // GET /api/projects/:projectId/board from Plan 24-01), the
    // server-derived canFlipVisibility from the GET /board response replaces
    // this temp value.
    canFlipVisibility: true,
    // Phase 49 Plan 49-05: POST response carries tierId but not nested tier.
    // Callers preserve the optimistic tier in onSuccess (above); on board
    // invalidation refetch the canonical tier object replaces this null.
    tier: null,
  };
}
